import type { ReactNode } from 'react'

import { Placeholder } from '@/src/elements/placeholder'
import { GENERAL_NOTIFICATION_SETTINGS } from '@/src/features/notification/models/notification.constants'
import { useNotificationSettingsMutation } from '@/src/features/notification/services/useNotificationSettingsMutation'
import { useNotificationSettingsQuery } from '@/src/features/notification/services/useNotificationSettingsQuery'
import { useAsyncToggle } from '@/src/general/hooks/useAsyncToggle'

import { SettingsCardSwitchInput } from '../SettingsCardSwitchInput'

type TMultipleSettingsCardGeneral = {
  updateParametersToken?: string
  type: 'email' | 'push'
  testID?: string
  label?: string
}

const allExclusiveToMailSettingsKeys = [
  GENERAL_NOTIFICATION_SETTINGS.NEWSLETTER,
  GENERAL_NOTIFICATION_SETTINGS.ON_GODSON_JOINED,
  GENERAL_NOTIFICATION_SETTINGS.ON_COMMUNITY_JOINED,
]

const allSharedSettingsKeys = [
  GENERAL_NOTIFICATION_SETTINGS.DIGEST,
  GENERAL_NOTIFICATION_SETTINGS.ON_INTENTION_FINISHED,
  GENERAL_NOTIFICATION_SETTINGS.ON_INTENTION_WATCHMAN,
  GENERAL_NOTIFICATION_SETTINGS.ON_PRIVATE_MESSAGE,
  GENERAL_NOTIFICATION_SETTINGS.ON_RETREAT_ENDED,
  GENERAL_NOTIFICATION_SETTINGS.ON_USER_CATCHUP,
]

export const MultipleSettingsCardGeneral = ({
  updateParametersToken,
  type,
  ...props
}: TMultipleSettingsCardGeneral): ReactNode => {
  const { data, isError } = useNotificationSettingsQuery(
    { isSubscriptionAlive: true, updateParametersToken },
    !!updateParametersToken,
  )
  const settingsKeys =
    type === 'email' ? [...allSharedSettingsKeys, ...allExclusiveToMailSettingsKeys] : allSharedSettingsKeys
  const { mutateAsync } = useNotificationSettingsMutation(settingsKeys, updateParametersToken)

  const isToggledOn = !!settingsKeys.find((key) => data?.[key]?.[type] === true)

  const [toggleValue, onChange, onToggleDisabled] = useAsyncToggle(isToggledOn, async (value) => {
    await mutateAsync({ [type]: value })
  })

  if (isError) return null

  if (!data) return <Placeholder className="h-40" />

  if (type === 'email') {
    return (
      <SettingsCardSwitchInput.Mail
        className="!items-start"
        onValueChange={onChange}
        value={toggleValue}
        disabled={onToggleDisabled}
        {...props}
      />
    )
  }
  if (type === 'push') {
    return (
      <SettingsCardSwitchInput.Push
        className="!items-start"
        onValueChange={onChange}
        value={toggleValue}
        disabled={onToggleDisabled}
        {...props}
      />
    )
  }
}
